import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2, X, Copy, MessageCircle, Calendar, Sparkles, ArrowRight, Check,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------
// PlanBookingLinkModal — 2-step modal:
//   STEP 1: Admin picks which days + hourly slots the client is
//           allowed to book. Grid runs 21 days forward, 09:00-22:00
//           in 1h buckets (matches backend schedule).
//   STEP 2: Backend generates the token with restrictions attached.
//           Admin sees the URL, WhatsApp button + Copy.
//
// If the admin picks NO slots at all, the link is generated with
// no restrictions (client sees the default 21-day / 09-22 grid).
// ---------------------------------------------------------------

const HOURS = Array.from({ length: 14 }, (_, i) => `${String(9 + i).padStart(2, "0")}:00`); // 09:00-22:00

const buildDayList = (nDays = 21) => {
  const out = [];
  const today = new Date();
  for (let i = 0; i < nDays; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const iso = d.toISOString().slice(0, 10);
    const wd = d.toLocaleDateString("es-MX", { weekday: "short" });
    const md = d.toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
    out.push({ iso, label: `${wd} ${md}` });
  }
  return out;
};

export const PlanBookingLinkModal = ({ plan, authHeader, onClose, variant = "created" }) => {
  const [step, setStep] = useState(1); // 1 = slot picker, 2 = link result
  const [linkData, setLinkData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [slots, setSlots] = useState({}); // { "YYYY-MM-DD": Set<"HH:MM"> }

  const days = useMemo(() => buildDayList(21), []);

  const toggleSlot = (iso, hh) => {
    setSlots((prev) => {
      const cur = new Set(prev[iso] || []);
      if (cur.has(hh)) cur.delete(hh); else cur.add(hh);
      return { ...prev, [iso]: cur };
    });
  };
  const toggleAllInDay = (iso) => {
    setSlots((prev) => {
      const cur = new Set(prev[iso] || []);
      if (cur.size === HOURS.length) return { ...prev, [iso]: new Set() };
      return { ...prev, [iso]: new Set(HOURS) };
    });
  };

  // Bulk: enable ALL hours for ALL 21 days (or clear if already all filled).
  const toggleAllEverything = () => {
    setSlots((prev) => {
      // If every day already has every hour → clear ALL
      const isFullyFilled = days.every((d) => (prev[d.iso]?.size || 0) === HOURS.length);
      if (isFullyFilled) return {};
      const filled = {};
      for (const d of days) filled[d.iso] = new Set(HOURS);
      return filled;
    });
  };

  // Bulk: for each of the next N days, pick 2-6 RANDOM slots.
  // This makes the schedule look scarce/organic. Already-selected days
  // are overwritten by this operation.
  const randomFillAll = () => {
    setSlots(() => {
      const next = {};
      for (const d of days) {
        const count = 2 + Math.floor(Math.random() * 5); // 2..6
        const pool = [...HOURS];
        // Fisher-Yates partial shuffle
        for (let i = pool.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [pool[i], pool[j]] = [pool[j], pool[i]];
        }
        next[d.iso] = new Set(pool.slice(0, count).sort());
      }
      return next;
    });
    toast.success("Horarios aleatorios generados (2-6 por día)");
  };
  const totalSelected = Object.values(slots).reduce((n, s) => n + s.size, 0);
  const daysSelected = Object.values(slots).filter((s) => s.size > 0).length;

  const generate = async () => {
    setLoading(true);
    setError("");
    // Build request payload from current slot selection.
    const allowedByDay = {};
    const allowedDays = [];
    for (const [iso, set] of Object.entries(slots)) {
      if (set.size > 0) {
        allowedByDay[iso] = Array.from(set).sort();
        allowedDays.push(iso);
      }
    }
    // If nothing selected → no restrictions (link opens full schedule).
    const payload = allowedDays.length
      ? { allowed_days: allowedDays, allowed_slots_by_day: allowedByDay }
      : {};
    try {
      const r = await axios.post(
        `${API}/admin/payment-plans/${plan.id}/booking-link`,
        payload,
        { headers: authHeader() },
      );
      setLinkData(r.data);
      setStep(2);
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo generar el enlace");
    } finally {
      setLoading(false);
    }
  };

  // On re-open (e.g. plan already has restrictions), load them into state.
  useEffect(() => {
    if (!plan) return;
    if (plan.allowed_slots_by_day && typeof plan.allowed_slots_by_day === "object") {
      const initial = {};
      for (const [iso, arr] of Object.entries(plan.allowed_slots_by_day)) {
        initial[iso] = new Set(arr || []);
      }
      setSlots(initial);
    }
  }, [plan]);

  if (!plan) return null;

  const copyUrl = () => {
    if (!linkData?.url) return;
    navigator.clipboard.writeText(linkData.url)
      .then(() => toast.success("Enlace copiado"))
      .catch(() => toast.error("No se pudo copiar"));
  };

  const waLink = () => {
    if (!linkData?.url) return "#";
    const rawPhone = linkData?.user?.phone || plan.user_phone || "";
    const digits = String(rawPhone).replace(/\D/g, "");
    const serviceName = linkData?.service?.name || plan.service_name || "tu servicio";
    const clientName = linkData?.user?.name || linkData?.user?.username || plan.user_name || "";
    // Trabajos que incluyen lectura como parte del servicio → mensaje distinto
    const sid = String(linkData?.service?.id || plan.service_id || "").toLowerCase();
    const includesReading = ["magia_suprema", "poder_maximo", "pacto_luz"].includes(sid);
    const msg = encodeURIComponent(
      includesReading
        ? `Hola ${clientName}, aquí puedes agendar la lectura que va incluida con tu trabajo de ${serviceName}: ${linkData.url} El enlace es único y personal para ti. Elige el día y la hora que mejor te acomoden. Bendiciones.`
        : `Hola ${clientName}, aquí puedes agendar tu ${serviceName}: ${linkData.url} El enlace es único y personal para ti. Elige el día y la hora que mejor te acomoden. Bendiciones.`
    );
    return digits ? `https://wa.me/${digits}?text=${msg}` : `https://wa.me/?text=${msg}`;
  };

  const introTitle = variant === "installment"
    ? "¡Abono registrado! Configura horarios y envía enlace"
    : "¡Servicio registrado! Configura horarios y envía enlace";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[150] bg-black/85 backdrop-blur-md flex items-center justify-center px-3 py-6 overflow-y-auto"
        data-testid="plan-booking-link-modal"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }}
          className="relative w-full max-w-2xl bg-gradient-to-br from-[#0a0510] to-black border-2 border-[#d4af37]/60 rounded-2xl p-5 my-auto shadow-[0_0_60px_rgba(212,175,55,0.35)]"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            data-testid="plan-booking-link-close"
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-[#d4af37]" />
            <h3 className="font-serif text-lg text-white">{introTitle}</h3>
          </div>

          <p className="text-xs text-neutral-400 mb-4">
            Cliente <b className="text-white">{plan.user_name}</b>
            {plan.user_visible_id ? <> · <span className="font-mono text-[#d4af37]">{plan.user_visible_id}</span></> : null}
            <br />
            Servicio: <b className="text-white">{plan.service_name}</b>
          </p>

          {step === 1 && (
            <>
              <div className="mb-3 p-3 rounded-lg bg-black/40 border border-[#d4af37]/20 text-[11px] text-neutral-300">
                <div className="text-[10px] uppercase tracking-widest text-[#d4af37]/90 mb-1">Paso 1 · Elige los horarios que estarán disponibles para el cliente</div>
                Marca los horarios en los días específicos donde puedes atender al cliente. Los horarios que NO marques aparecerán bloqueados cuando el cliente abra el enlace. Si no marcas nada, el cliente verá todos los horarios estándar (09:00-22:00 los próximos 21 días).
              </div>

              {/* Bulk actions: select all + random 2-6 per day */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <button
                  type="button"
                  data-testid="slot-bulk-all"
                  onClick={toggleAllEverything}
                  className="text-[10px] px-3 py-1.5 rounded-full border border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37]/10 uppercase tracking-widest"
                >
                  Seleccionar todos
                </button>
                <button
                  type="button"
                  data-testid="slot-bulk-random"
                  onClick={randomFillAll}
                  className="text-[10px] px-3 py-1.5 rounded-full border border-purple-400/50 text-purple-300 hover:bg-purple-500/10 uppercase tracking-widest flex items-center gap-1.5"
                  title="Genera 2-6 horarios al azar por cada día (aparenta agenda apretada)"
                >
                  <Sparkles className="w-3 h-3" /> Aleatorio (2-6 por día)
                </button>
                <button
                  type="button"
                  data-testid="slot-bulk-clear"
                  onClick={() => setSlots({})}
                  className="text-[10px] px-3 py-1.5 rounded-full border border-white/15 text-neutral-400 hover:text-white hover:bg-white/5 uppercase tracking-widest"
                >
                  Limpiar
                </button>
              </div>

              <div className="max-h-[45vh] overflow-y-auto pr-1 space-y-1.5">
                {days.map((d) => {
                  const sel = slots[d.iso] || new Set();
                  const allSel = sel.size === HOURS.length;
                  return (
                    <div key={d.iso} className="rounded-lg border border-white/5 bg-black/30 p-2">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="text-[11px] text-neutral-200 font-medium capitalize">{d.label}</div>
                        <button
                          type="button"
                          data-testid={`slot-day-all-${d.iso}`}
                          onClick={() => toggleAllInDay(d.iso)}
                          className={`text-[9px] px-2 py-0.5 rounded ${allSel ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : "bg-white/5 border border-white/10 text-neutral-400"}`}
                        >
                          {allSel ? "Quitar todo" : "Todo el día"}
                        </button>
                      </div>
                      <div className="grid grid-cols-7 gap-1">
                        {HOURS.map((hh) => {
                          const active = sel.has(hh);
                          return (
                            <button
                              key={hh}
                              type="button"
                              data-testid={`slot-${d.iso}-${hh}`}
                              onClick={() => toggleSlot(d.iso, hh)}
                              className={`text-[10px] py-1 rounded border transition ${active ? "bg-[#d4af37] text-black border-[#d4af37] font-bold" : "bg-black/40 border-white/10 text-neutral-400 hover:border-[#d4af37]/50"}`}
                            >
                              {hh}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="text-[10px] text-neutral-500">
                  {totalSelected > 0 ? (
                    <span className="text-emerald-300">✓ {totalSelected} horario{totalSelected !== 1 ? "s" : ""} en {daysSelected} día{daysSelected !== 1 ? "s" : ""}</span>
                  ) : (
                    <span>Sin restricciones — el cliente verá todos los horarios</span>
                  )}
                </div>
                <button
                  type="button"
                  data-testid="plan-booking-link-generate"
                  disabled={loading}
                  onClick={generate}
                  className="btn-gold px-4 py-2 rounded-full flex items-center gap-1.5 uppercase text-[10px] tracking-widest font-bold disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                  Generar enlace
                </button>
              </div>
              {error && (
                <div className="mt-2 text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2">{error}</div>
              )}
            </>
          )}

          {step === 2 && linkData && (
            <>
              <div className="mb-2 p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-[11px] text-emerald-300 flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5" /> Paso 2 · Enlace listo · {totalSelected > 0 ? `${totalSelected} horarios habilitados` : "Sin restricciones"}
              </div>

              <div className="rounded-xl border border-[#d4af37]/40 bg-black/60 px-3 py-3 mb-3">
                <div className="text-[10px] uppercase tracking-widest text-[#d4af37]/80 mb-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Enlace de agendado {linkData.reused ? "(reutilizado — aún válido)" : "(nuevo)"}
                </div>
                <div
                  className="text-[11px] text-white font-mono break-all select-all leading-relaxed"
                  data-testid="plan-booking-link-url"
                >
                  {linkData.url}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  data-testid="plan-booking-link-copy"
                  onClick={copyUrl}
                  className="btn-outline-gold py-2.5 rounded-full flex items-center justify-center gap-1.5 uppercase text-[10px] tracking-widest"
                >
                  <Copy className="w-3.5 h-3.5" /> Copiar
                </button>
                <a
                  data-testid="plan-booking-link-whatsapp"
                  href={waLink()}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-gold py-2.5 rounded-full flex items-center justify-center gap-1.5 uppercase text-[10px] tracking-widest"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> Enviar por WhatsApp
                </a>
              </div>

              <div className="text-[10px] text-neutral-500 border-t border-white/5 pt-2">
                Expira: {linkData.expires_at
                  ? new Date(linkData.expires_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })
                  : "—"}
                {" · "}
                Este enlace es único y se marca como usado al agendar.
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PlanBookingLinkModal;
